import { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../utils/axios';
import {
  DEFAULT_LIST_PAGE,
  DEFAULT_LIST_PAGE_SIZE,
  DEFAULT_LOOKUP_PAGE_SIZE,
} from '../constants';
import type {
  MarketRecord,
  PagedResult,
  TemplateRecord,
  TemplateTotalsParams,
} from '../types/index';

type UseTemplatesParams = TemplateTotalsParams & {
  enabled?: boolean;
};

type UseTemplatesResult = {
  data: TemplateRecord[];
  error: unknown;
  isLoading: boolean;
  refetch: () => Promise<void>;
  total: number;
};

function mapTemplateRecord(
  template: any,
  marketsById: Record<string, MarketRecord> = {}
): TemplateRecord {
  const marketIds = Array.isArray(template?.marketIds) ? template.marketIds : [];
  const mappedMarkets = marketIds
    .map((id) => marketsById[String(id)]?.name || marketsById[String(id)]?.code)
    .filter(Boolean);

  return {
    ...template,
    id: template?.id,
    name: template?.name || '',
    marketIds,
    markets: mappedMarkets,
    market: mappedMarkets[0] || '',
  };
}

function buildMarketsById(markets: MarketRecord[] = []) {
  return Object.fromEntries((markets || []).map((market) => [String(market.id), market]));
}

function normalizePagedTemplates(
  payload: any,
  page: number,
  pageSize: number,
  marketsById: Record<string, MarketRecord>
): PagedResult<TemplateRecord> {
  const source = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.documents)
      ? payload.documents
      : Array.isArray(payload)
        ? payload
        : [];

  return {
    page: Number(payload?.page ?? page),
    pageSize: Number(payload?.pageSize ?? pageSize),
    total: Number(payload?.total ?? source.length ?? 0),
    data: source.map((template) => mapTemplateRecord(template, marketsById)),
  };
}

export async function getTemplates({
  organizationId,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LOOKUP_PAGE_SIZE,
  active = true,
  brandId = null,
  markets = [],
}: {
  organizationId: number | string;
  page?: number;
  pageSize?: number;
  active?: boolean | null;
  brandId?: number | string | null;
  markets?: MarketRecord[];
}): Promise<TemplateRecord[]> {
  const response = await axios.get('/template', {
    params: { organizationId, page, pageSize, active, brandId },
  });
  const marketsById = buildMarketsById(markets);
  const data = Array.isArray(response.data) ? response.data : [];
  return data.map((template) => mapTemplateRecord(template, marketsById));
}

export async function getAllTemplates({
  organizationId,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LOOKUP_PAGE_SIZE,
  marketId = null,
  brandId = null,
  name = null,
  active = true,
  markets = [],
}: {
  organizationId: number | string;
  page?: number;
  pageSize?: number;
  marketId?: number | string | null;
  brandId?: number | string | null;
  name?: string | null;
  active?: boolean | null;
  markets?: MarketRecord[];
}): Promise<TemplateRecord[]> {
  const response = await axios.get('/template/all', {
    params: { organizationId, page, pageSize, marketId, brandId, name, active },
  });
  const marketsById = buildMarketsById(markets);
  const data = Array.isArray(response.data) ? response.data : [];
  return data.map((template) => mapTemplateRecord(template, marketsById));
}

export async function getTemplatesTotals({
  organizationId,
  marketId = null,
  brandId = null,
  name = null,
  active = true,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
  sortField = null,
  sortAscending = null,
  lastModifiedSortAscending = null,
  markets = [],
}: TemplateTotalsParams & {
  markets?: MarketRecord[];
} = {}): Promise<PagedResult<TemplateRecord>> {
  const response = await axios.get('/template/totals', {
    params: {
      organizationId,
      marketId,
      brandId,
      name,
      page,
      pageSize,
      active,
      sortField,
      sortAscending,
      lastModifiedSortAscending,
    },
  });

  return normalizePagedTemplates(response.data, page, pageSize, buildMarketsById(markets));
}

export async function getTemplateById({
  organizationId,
  templateId,
  markets = [],
}: {
  organizationId: number | string;
  templateId: number | string;
  markets?: MarketRecord[];
}): Promise<TemplateRecord | null> {
  const response = await axios.get('/template/byid', {
    params: { organizationId, templateId },
  });
  return response.data
    ? mapTemplateRecord(response.data, buildMarketsById(markets))
    : null;
}

export async function addTemplate(payload: unknown): Promise<unknown> {
  const response = await axios.post('/template/add', payload);
  return response.data;
}

export async function editTemplate(payload: unknown): Promise<unknown> {
  const response = await axios.post('/template/edit', payload);
  return response.data;
}

export async function removeTemplate(templateId: number | string): Promise<unknown> {
  const response = await axios.delete('/template/remove', {
    params: { templateId },
  });
  return response.data;
}

export async function unarchiveTemplate(templateId: number | string): Promise<unknown> {
  const numericTemplateId = Number(templateId);
  if (!Number.isSafeInteger(numericTemplateId) || numericTemplateId <= 0) {
    throw new TypeError('Template ID must be a positive integer.');
  }

  const response = await axios.post('/template/unarchive', JSON.stringify(numericTemplateId), {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
}

export default function useTemplates({
  organizationId,
  marketId = null,
  brandId = null,
  name = null,
  active = true,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
  enabled = true,
}: UseTemplatesParams = {}): UseTemplatesResult {
  const [data, setData] = useState<TemplateRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<unknown>(null);
  const requestIdRef = useRef(0);

  const refetch = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!enabled || !organizationId) {
      setData([]);
      setTotal(0);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const marketsResponse = await axios.get('/market/all', {
        params: { organizationId },
      });
      const markets = Array.isArray(marketsResponse.data) ? marketsResponse.data : [];
      const results = await getTemplatesTotals({
        organizationId,
        marketId,
        brandId,
        name,
        active,
        page,
        pageSize,
        markets,
      });
      if (requestId === requestIdRef.current) {
        setData(results.data);
        setTotal(results.total);
      }
    } catch (nextError) {
      if (requestId === requestIdRef.current) {
        setError(nextError);
        setData([]);
        setTotal(0);
      }
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, [active, brandId, enabled, marketId, name, organizationId, page, pageSize]);

  useEffect(() => {
    void refetch();
    return () => { requestIdRef.current += 1; };
  }, [refetch]);

  return {
    data,
    error,
    isLoading,
    refetch,
    total,
  };
}
